import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Lightbulb, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CardActions } from "@/components/CardActions";
import { CinematicVideo } from "@/components/CinematicVideo";

const DYK_VIDEO =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/yqt5w61i_gemini_generated_video_dfebd9a4.mp4";

export function DidYouKnow() {
  const { t, i18n } = useTranslation();
  const [fact, setFact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [domainIdx, setDomainIdx] = useState(null);

  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const { data } = await api.get("/public/fact", { params: { refresh, lang: i18n.language } });
      setFact(data.fact);
      setDomainIdx(typeof data.domain_index === "number" ? data.domain_index : null);
    } catch (e) {
      if (refresh) toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("atelier.factError"));
      setFact(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(false); }, [i18n.language]); // eslint-disable-line

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
      data-testid="did-you-know"
      className="max-w-3xl mx-auto glass stats-wow rounded-2xl px-7 py-9 text-center"
    >
      <div className="flex flex-col items-center">
        <div className="max-w-xl w-full mx-auto mb-8">
          <CinematicVideo
            src={DYK_VIDEO}
            testId="dyk-cinematic-video"
            caption={t("atelier.factTitle")}
            fade
            noSound
          />
        </div>
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <Lightbulb className="text-primary pulse-star" size={22} strokeWidth={1.7} />
        </div>
        <p className="uppercase tracking-[0.25em] text-xs text-primary/80 mb-2">{t("atelier.factTitle")}</p>
        {domainIdx !== null && !loading && (
          <p className="text-[11px] text-foreground/40 mb-4" data-testid="dyk-domain">
            {t("atelier.factToday")} · <span className="text-primary/70">{(t("atelier.factDomains", { returnObjects: true }) || [])[domainIdx]}</span>
          </p>
        )}
        {loading && !fact ? (
          <p className="font-display italic text-foreground/40 flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> {t("atelier.factLoading")}
          </p>
        ) : (
          <p className="font-display italic text-lg sm:text-xl text-foreground/85 leading-relaxed max-w-2xl mx-auto" data-testid="dyk-text">
            {fact || t("atelier.factError")}
          </p>
        )}
        <button
          onClick={() => load(true)} disabled={loading}
          className="mt-6 inline-flex items-center gap-1.5 text-xs text-foreground/45 hover:text-primary transition-colors disabled:opacity-50"
          data-testid="dyk-refresh"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> {t("atelier.factNew")}
        </button>
        {fact && <CardActions cardId="fact" heading={fact} testId="dyk" />}
      </div>
    </motion.div>
  );
}
